import { mdiChevronRight, mdiMinus, mdiPlus, mdiShieldCheck } from '@mdi/js';
import { useTranslation } from 'react-i18next';

import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemFooter,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { ICertificateTypeListItemFragment } from 'graphql/types';
import { ChipGroup } from 'common/components/Chip';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';

interface IProps extends React.ComponentProps<typeof ListItem> {
    addButton?: boolean;
    added?: boolean;
    disabled?: boolean;
    selected?: boolean;
    certificate: ICertificateTypeListItemFragment;
    footer?: React.ReactNode;
    onClick?(certificate: ICertificateTypeListItemFragment): void;
}

export const CertificateSelectorListItem = ({
    addButton,
    added,
    disabled,
    selected,
    certificate,
    footer,
    onClick,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { id, name, thumbnail, extraCategoryValues } = certificate || {};

    const isDisabled = disabled || (addButton && added);

    let mediaIconPath = mdiShieldCheck;

    if (addButton && !added) {
        mediaIconPath = selected ? mdiMinus : mdiPlus;
    }

    return (
        <ListItem
            button={!isDisabled}
            key={id}
            px={0}
            selected={!isDisabled && selected}
            sx={isDisabled ? { opacity: 0.5, cursor: 'default' } : undefined}
            onClick={() => !isDisabled && onClick?.(certificate)}
            {...other}
        >
            <ListItemMedia
                color="primary"
                image={thumbnail?.url || ''}
                size="small"
            >
                <Icon path={mediaIconPath} size="3rem" />
            </ListItemMedia>

            <ListItemText primary={name}>
                {!!extraCategoryValues?.length && (
                    <ChipGroup sx={{ my: 0.25 }}>
                        <ExtraCategoryLabels
                            chipProps={{ size: 'small' }}
                            extraCategoryValues={extraCategoryValues}
                        />
                    </ChipGroup>
                )}
            </ListItemText>

            {!addButton && !isDisabled && (
                <>
                    <ListItemActionText>{translate('open')}</ListItemActionText>
                    <ListItemSecondaryAction>
                        <Icon path={mdiChevronRight} size="3rem" />
                    </ListItemSecondaryAction>
                </>
            )}

            {footer && <ListItemFooter>{footer}</ListItemFooter>}
        </ListItem>
    );
};
